import { useCallback, useContext } from 'react';
import { useDrop } from 'react-dnd';
import PageState from '../Shared/pageState';
import ControlState from '../Shared/controlState';
import ControlKind from '../Shared/controlKind';
import { put } from '../Shared/ajax';
import { getErrorMessage } from '../Shared/errorHandling';
import { useSnackbar } from 'notistack';
import update from 'immutability-helper';
import { JSONSchema7 } from 'json-schema';
import JSONSchema7Properties from '../Shared/jSONSchema7Properties';
import {
  WorkflowEngineActionKind,
  WorkflowEngineContext
} from '../WorkflowEngine/workflowEngineReducer';
import {
  DragTypes,
  DraggableItem,
  generateSchema,
  DraggableCardControl,
  PageCardControl
} from '../DragAndDrop';
import changeSchemaOrder from './changeSchemaOrder';
import Grid from '@mui/material/Grid';

interface UpdatePageRequest {
  externalId: string;
  metadata: string;
}

interface UpdatePageResponse {
  externalId: string;
}

const PageEngine = (page: PageState) => {
  if (page.controls.length === 0) return null;

  const { state, dispatch } = useContext(WorkflowEngineContext);
  const { enqueueSnackbar } = useSnackbar();

  const savePageSchema = async (pageId: string, pageSchema: JSONSchema7) => {
    try {
      await put<UpdatePageResponse, UpdatePageRequest>('DynamicWorkflowPage', {
        externalId: pageId,
        metadata: JSON.stringify(pageSchema)
      });
      enqueueSnackbar('Configuration successfully saved!', {
        variant: 'success'
      });
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
  };

  const findCard = useCallback(
    (id: string) => {
      const card = page.controls.filter((c) => c.id === id)[0];
      return {
        card,
        index: page.controls.indexOf(card)
      };
    },
    [page]
  );

  const moveCard = useCallback(
    (id: string, atIndex: number) => {
      const { card, index } = findCard(id);

      const updatedControls = update(page.controls, {
        $splice: [
          [index, 1],
          [atIndex, 0, card]
        ]
      });

      const newPageSchema = page.schema;
      newPageSchema.properties = changeSchemaOrder(updatedControls);

      dispatch({
        type: WorkflowEngineActionKind.PAGE_UPDATE,
        payload: {
          id: page.id,
          schema: newPageSchema,
          controls: updatedControls
        } as PageState
      });
    },
    [findCard, page, dispatch]
  );

  const updatePage = async (newControl: ControlState, newIndex: number) => {
    const updatedControls = update(page.controls, {
      $splice: [[newIndex, 0, newControl]]
    });

    const newPageSchema = page.schema;
    newPageSchema.properties = changeSchemaOrder(updatedControls);

    dispatch({
      type: WorkflowEngineActionKind.PAGE_UPDATE,
      payload: {
        id: page.id,
        schema: newPageSchema,
        controls: updatedControls
      } as PageState
    });
  };

  const createNewCard = useCallback(
    (type: ControlKind, pageId: string, index: number) => {
      const newControl = generateSchema(pageId, type, index);
      if (newControl && newControl.schema) {
        updatePage(newControl, index);
      }
    },
    [updatePage]
  );

  const [, drop] = useDrop(() => ({
    accept: [DragTypes.CARD_CONTROL, DragTypes.PALETTE_CONTROL],
    drop(item: DraggableItem) {
      if (item.pageId === page.id) {
        savePageSchema(page.id, page.schema);
      }
    }
  }));

  return (
    <Grid container key={page.id} ref={drop}>
      {page.controls.map((control: ControlState) => {
        if (control.id === 'page') {
          return (
            <Grid key={`${control.pageId}-${control.id}`} item xs={12}>
              <PageCardControl
                key={`${control.pageId}-${control.id}`}
                pageId={control.pageId}
                createNewCard={createNewCard}
                control={control}
              />
            </Grid>
          );
        }

        const gridSize = (control.schema?.properties as JSONSchema7Properties)[
          'gridSize'
        ]?.default;
        return (
          <Grid
            key={`${control.pageId}-${control.id}`}
            item
            xs={gridSize && !control.building ? (gridSize as number) : 12}
          >
            <DraggableCardControl
              key={`${control.pageId}-${control.id}`}
              id={control.id}
              pageId={control.pageId}
              moveCard={moveCard}
              findCard={findCard}
              createNewCard={createNewCard}
              control={control}
              isReadOnlyView={state.isReadOnlyView}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default PageEngine;
